import { supabase } from './supabase';
import { SUPABASE_URL, SUPABASE_ANON_KEY as ANON_KEY } from './env';
import { logError } from '@/utils/errorHandler';

export interface CallEdgeOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  timeoutMs?: number;
  retries?: number;
  headers?: Record<string, string>;
}

export class EdgeCallError extends Error {
  status: number;
  code?: string;
  /**
   * 엣지 함수가 돌려준 응답 본문 전체(JSON 인 경우).
   *
   * message·code 만 꺼내 두면 `required_credits`·`current_balance`·
   * `fal_request_id` 같은 값이 사라진다. 그런 값들이 "얼마가 더 필요한지",
   * "지원 문의에 무엇을 붙여야 하는지"를 화면에 쓰게 해 준다.
   */
  body?: Record<string, unknown>;
  constructor(message: string, status: number, code?: string, body?: Record<string, unknown>) {
    super(message);
    this.name = 'EdgeCallError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

const DEFAULT_TIMEOUT_MS = 60_000;

function buildUrl(fn: string, query?: CallEdgeOptions['query']): string {
  const url = new URL(`${SUPABASE_URL}/functions/v1/${fn}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function getAuthHeader(): Promise<Record<string, string>> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token
      ? { Authorization: `Bearer ${session.access_token}` }
      : { Authorization: `Bearer ${ANON_KEY}` };
  } catch (err) {
    logError(err, { where: 'edgeClient.getAuthHeader' }, 'warn');
    return { Authorization: `Bearer ${ANON_KEY}` };
  }
}

/**
 * 비동기로 현재 세션의 access_token을 반환합니다.
 * getAuthorizationHeader() (동기 localStorage 방식)를 대체하는
 * 안전한 비동기 버전입니다.
 *
 * 사용 예:
 *   const token = await getAuthToken();
 *   fetch(url, { headers: { Authorization: token, 'Content-Type': 'application/json' } });
 *
 * Edge Function이 아닌 직접 fetch를 사용하는 컴포넌트/훅에서 활용하세요.
 * Edge Function 호출에는 callEdge()를 우선 사용하세요.
 */
export async function getAuthToken(): Promise<string> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) return `Bearer ${session.access_token}`;
  } catch { /* fallback */ }
  return `Bearer ${ANON_KEY}`;
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    // A listener added after cancellation will never receive the old event.
    if (signal?.aborted) { reject(signal.reason); return; }
    const cleanup = () => signal?.removeEventListener('abort', onAbort);
    const onAbort = () => {
      clearTimeout(timer);
      cleanup();
      reject(signal?.reason);
    };
    const timer = setTimeout(() => { cleanup(); resolve(); }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Supabase Edge Function 호출 공통 래퍼.
 * - 세션이 있으면 user access_token, 없으면 anon key 사용
 * - 타임아웃 + AbortSignal 지원
 * - HTTP 5xx 응답에 한해 재시도(지수 백오프). 4xx/네트워크/타임아웃/파싱 오류는 재전송하지 않는다.
 * - GET만 기본 1회 재시도. 변경/과금 요청은 서버 멱등성이 확인된 호출부에서만 명시적으로 켠다.
 */
export async function callEdge<T = unknown>(fn: string, options: CallEdgeOptions = {}): Promise<T> {
  const {
    method = 'POST',
    body,
    query,
    signal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retries = method === 'GET' ? 1 : 0,
    headers: extraHeaders = {},
  } = options;

  signal?.throwIfAborted();
  const url = buildUrl(fn, query);
  const authHeaders = await getAuthHeader();

  for (let attempt = 0; ; attempt += 1) {
    // Cancellation can happen while getSession() or the retry backoff is pending.
    signal?.throwIfAborted();
    const attemptController = new AbortController();
    const onParentAbort = () => attemptController.abort(signal?.reason);
    signal?.addEventListener('abort', onParentAbort, { once: true });
    const timeoutId = setTimeout(() => attemptController.abort(), timeoutMs);

    try {
      // FormData(파일 업로드)는 그대로 넘기고 Content-Type 도 브라우저가 boundary 와 함께 붙이게 둔다.
      // 과거엔 무조건 JSON.stringify 해서 FormData 가 "{}" 로 나갔고(clone-voice 가 항상
      // "Expected multipart/form-data" 로 실패), 수동 Content-Type 은 boundary 가 빠져 파싱이 깨진다.
      const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
      const resp = await fetch(url, {
        method,
        headers: {
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          apikey: ANON_KEY,
          ...authHeaders,
          ...extraHeaders,
        },
        body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
        signal: attemptController.signal,
      });

      if (!resp.ok) {
        let code: string | undefined;
        let msg = `HTTP ${resp.status}`;
        // 본문 전체를 들고 간다 — 엣지 함수가 준 한국어 안내와 부가 정보
        // (required_credits, fal_request_id 등)를 화면에서 쓸 수 있게.
        let payload: Record<string, unknown> | undefined;
        try {
          const errJson = await resp.json() as { error?: string; message?: string; code?: string };
          // `error` 가 기계용 코드일 때는 사람이 읽을 `message` 를 쓴다.
          // 레이트리밋 응답은 error='rate_limited' + message='오늘 한도에 도달했어요…' 형태라,
          // error 를 먼저 집으면 사용자에게 영어 코드 'rate_limited' 만 보였다.
          const machineCode = /^[a-z0-9_]+$/.test(errJson.error ?? '');
          msg = (machineCode && errJson.message) ? errJson.message : (errJson.error ?? errJson.message ?? msg);
          code = errJson.code;
          if (errJson && typeof errJson === 'object') payload = errJson as Record<string, unknown>;
        } catch { /* 응답 본문이 JSON이 아님 */ }

        throw new EdgeCallError(msg, resp.status, code, payload);
      }

      const text = await resp.text();
      signal?.throwIfAborted();
      return (text ? JSON.parse(text) : null) as T;
    } catch (err) {
      signal?.throwIfAborted();
      // The old catch also retried the 4xx thrown above and successful responses
      // with invalid JSON. Neither proves that a paid operation was not executed.
      const canRetry = err instanceof EdgeCallError
        && err.status >= 500 && err.status < 600 && attempt < retries;
      if (!canRetry) {
        logError(err, { where: 'callEdge', fn, attempt }, 'error');
        throw err;
      }
    } finally {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onParentAbort);
    }
    // Release the attempt timer/listener before entering the cancellable backoff.
    await wait(2 ** attempt * 500, signal);
  }
}

export interface EdgeFailure {
  /** 사용자에게 보여 줄 문구. 서버가 준 것이 있으면 그것, 없으면 fallback. */
  message: string;
  /** 기계가 분기할 값 — 'insufficient_credits', 'request_in_flight' 등. */
  code?: string;
  /** 공급자 원인 등 부가 설명. 있으면 문구 아래 한 줄로 덧붙인다. */
  detail?: string;
  /** 응답 본문 전체 — required_credits 같은 값이 필요할 때. */
  body?: Record<string, unknown>;
}

/**
 * 실패 하나를 읽어 화면에 쓸 수 있는 모양으로 만든다.
 *
 * 엣지 함수는 오류를 **두 가지 모양**으로 준다:
 *   · 비-2xx + `{ error, code, ... }`  → invokeEdge 가 EdgeCallError 로 준다
 *   · 200 + `{ error: '...' }`         → data 안에 들어 있다
 *
 * 호출부마다 이 둘을 손으로 합치다 보니 같은 코드가 **11벌** 생겼고, 전부
 * 조금씩 달랐다(어떤 곳은 code 를 안 읽어 "크레딧 부족"과 "서버 죽음"을
 * 구분하지 못했고, 어떤 곳은 detail 을 버렸다). 한 곳으로 모은다.
 */
export function edgeFailure(error: unknown, data: unknown, fallback: string): EdgeFailure {
  // 오류 타입을 호출부마다 다르게 선언해 둔 곳이 많아 관용적으로 받는다 —
  // 여기서 타입을 깐깐하게 굴면 각 화면이 다시 자기만의 처리를 만들게 된다.
  const err = (error && typeof error === 'object' ? error : {}) as Partial<EdgeCallError>;
  const payload = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const body = err.body ?? (typeof payload.error === 'string' ? payload : undefined);
  const pick = (k: string): string | undefined => {
    const fromBody = body?.[k];
    if (typeof fromBody === 'string' && fromBody) return fromBody;
    const fromData = payload[k];
    return typeof fromData === 'string' && fromData ? fromData : undefined;
  };
  // callEdge 와 같은 규칙: `error` 가 기계용 코드('rate_limited' 등)이고 사람이 읽을
  // `message` 가 따로 있으면 그쪽을 쓴다. 여기만 빠져 있어 폴링 경로에서는 여전히
  // "영상 폴링 연속 오류: rate_limited" 가 화면에 떴다.
  const rawError = pick('error');
  const humanError = rawError && /^[a-z0-9_]+$/.test(rawError) && pick('message') ? pick('message') : rawError;
  return {
    // error.message 는 이미 본문에서 꺼낸 값이지만, 200+{error} 경로에서는
    // error 자체가 없으므로 data 쪽도 함께 본다.
    message: humanError ?? pick('message') ?? err.message ?? fallback,
    code: pick('code') ?? err.code,
    detail: pick('detail'),
    body,
  };
}

/**
 * 응답 본문의 기본 타입.
 *
 * `functions.invoke` 와 같은 느슨함을 일부러 유지한다 — 이 계층의 목적은
 * **오류를 잃지 않는 것**이지 타입 정비가 아니다. 40군데 응답 모양을 여기서
 * 지어내면 실제와 다른 타입이 박히고, 그건 타입이 없느니만 못하다.
 * 모양을 아는 호출부는 `invokeEdge<MyResponse>(...)` 로 좁히면 된다.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EdgePayload = Record<string, any>;

/**
 * `supabase.functions.invoke` 를 대체한다. 결과 모양은 그대로 `{ data, error }`
 * 이지만, **오류를 잃지 않는다.**
 *
 * supabase-js 의 invoke 는 2xx 가 아니면 `FunctionsHttpError` 를 만들고 그
 * message 를 `'Edge Function returned a non-2xx status code'` 로 고정한다.
 * 실제 응답 본문은 `error.context`(Response) 안에 들어가 버린다.
 *
 * 우리 엣지 함수는 **한국어 안내를 HTTP 상태 코드와 함께** 돌려준다:
 *
 *   402 { error: '크레딧이 부족합니다.', code: 'insufficient_credits',
 *         required_credits: 150, current_balance: 20 }
 *
 * 그런데 호출부는 대부분 `if (error) throw new Error(error.message)` 였다.
 * 그래서 크레딧이 모자란 사용자에게 뜬 문구가 실제로 이랬다 —
 * **"Edge Function returned a non-2xx status code".** 무엇이 부족한지도,
 * 얼마가 필요한지도, 충전하면 된다는 사실도 화면에 없었다.
 *
 * 여기서는 callEdge 를 그대로 쓴다. 그러면 본문에서 꺼낸 진짜 메시지·code·
 * status 가 담긴 EdgeCallError 가 오고, 타임아웃도 함께 붙는다. (invoke 에는
 * 클라이언트 타임아웃이 없어 함수가 멈추면 스피너가 영원히 돈다.)
 *
 * **재시도는 기본으로 켜지 않는다.** GET을 포함해 invokeEdge의 기존 기본값은 유지한다.
 * 여기로 옮겨 온 호출부의 대부분이 크레딧을 쓰는 생성 요청이다. 함수가 차감을
 * 끝낸 뒤 5xx 로 죽으면 재시도는 두 번째 차감이 된다. 멱등 키를 함께 보내는
 * 호출부만 `retries` 를 명시해 켜면 된다.
 */
export async function invokeEdge<T = EdgePayload>(
  fn: string,
  options: CallEdgeOptions = {},
): Promise<{ data: T | null; error: EdgeCallError | null }> {
  try {
    return { data: await callEdge<T>(fn, { retries: 0, ...options }), error: null };
  } catch (err) {
    if (err instanceof EdgeCallError) return { data: null, error: err };
    // 네트워크 끊김·타임아웃 등 — HTTP 응답을 확정할 수 없어 status 0.
    // 서버가 요청을 처리했을 수도 있다. 미과금/환불 완료로 해석하면 안 된다.
    const message = err instanceof Error ? err.message : String(err);
    return { data: null, error: new EdgeCallError(message, 0) };
  }
}
